import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';
import { useContactForm } from '../context/ContactFormContext';

const Header = () => {
  const { t, i18n } = useTranslation();
  const { open } = useContactForm();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    setIsLangMenuOpen(false);
  };

  const navLinks = [
    { href: '#courses', label: t('nav.courses') },
    { href: '#about', label: t('nav.about') },
    { href: '#faq', label: t('nav.faq') },
    { href: '#footer', label: t('nav.contact') },
  ];

  const currentLang = i18n.language === 'ru' ? 'RU' : 'EN';

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-crypto-black/90 backdrop-blur-xl border-b border-crypto-red/20'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <motion.a
              href="#"
              className="flex items-center gap-3 group"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-crypto-red to-crypto-gradient-end flex items-center justify-center">
                <span className="text-white font-bold text-xl">R</span>
              </div>
              <div className="hidden sm:block">
                <span className="text-xl font-bold text-white">RedLevel</span>
                <span className="text-crypto-red font-bold text-xl">Crypto</span>
              </div>
            </motion.a>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              {navLinks.map((link, index) => (
                <motion.a
                  key={link.href}
                  href={link.href}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="text-gray-300 hover:text-crypto-red transition-colors duration-300 text-sm font-medium"
                >
                  {link.label}
                </motion.a>
              ))}
            </nav>

            {/* Language Selector & CTA */}
            <div className="hidden md:flex items-center gap-4">
              {/* Language Dropdown */}
              <div className="relative">
                <motion.button
                  onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-crypto-card hover:bg-crypto-card/80 transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Globe className="w-4 h-4 text-crypto-red" />
                  <span className="text-sm font-medium">{currentLang}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`} />
                </motion.button>

                <AnimatePresence>
                  {isLangMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute top-full right-0 mt-2 w-32 bg-crypto-card border border-crypto-red/20 rounded-xl overflow-hidden shadow-2xl"
                    >
                      <button
                        onClick={() => toggleLanguage('ru')}
                        className={`w-full px-4 py-3 text-left text-sm hover:bg-crypto-red/10 transition-colors ${
                          i18n.language === 'ru' ? 'text-crypto-red' : 'text-gray-300'
                        }`}
                      >
                        Русский
                      </button>
                      <button
                        onClick={() => toggleLanguage('en')}
                        className={`w-full px-4 py-3 text-left text-sm hover:bg-crypto-red/10 transition-colors ${
                          i18n.language === 'en' ? 'text-crypto-red' : 'text-gray-300'
                        }`}
                      >
                        English
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* CTA Button */}
              <motion.button
                onClick={() => open()}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-6 py-3 bg-gradient-to-r from-crypto-red to-crypto-gradient-end text-white font-bold rounded-xl hover:shadow-lg hover:shadow-crypto-red/30 transition-all duration-300"
              >
                {t('hero.ctaPrimary')}
              </motion.button>
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-white"
              whileTap={{ scale: 0.9 }}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-crypto-card/95 backdrop-blur-xl border-t border-crypto-red/20"
            >
              <div className="px-4 py-6 space-y-4">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block py-3 text-gray-300 hover:text-crypto-red transition-colors text-lg"
                  >
                    {link.label}
                  </a>
                ))}

                {/* Mobile Language Selector */}
                <div className="flex gap-4 pt-4 border-t border-gray-700">
                  <button
                    onClick={() => toggleLanguage('ru')}
                    className={`flex-1 py-3 rounded-lg text-center font-medium transition-colors ${
                      i18n.language === 'ru'
                        ? 'bg-crypto-red text-white'
                        : 'bg-crypto-dark text-gray-300'
                    }`}
                  >
                    RU
                  </button>
                  <button
                    onClick={() => toggleLanguage('en')}
                    className={`flex-1 py-3 rounded-lg text-center font-medium transition-colors ${
                      i18n.language === 'en'
                        ? 'bg-crypto-red text-white'
                        : 'bg-crypto-dark text-gray-300'
                    }`}
                  >
                    EN
                  </button>
                </div>

                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    open();
                  }}
                  className="block w-full py-4 bg-gradient-to-r from-crypto-red to-crypto-gradient-end text-white font-bold rounded-xl text-center mt-4"
                >
                  {t('hero.ctaPrimary')}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      {/* Click outside to close lang menu */}
      {isLangMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsLangMenuOpen(false)}
        />
      )}
    </>
  );
};

export default Header;
